import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect, Option } from "@/components/ui/multi-select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { useCreateTeenAccess } from "@/lib/api/teen-access";
import { cn } from "@/lib/utils";
import { FlagIcon, StarIcon, UserIcon } from "@heroicons/react/24/outline";
import { Controller, useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface AddAccessRecordDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  menteeId: string;
}

interface AccessRecordFormData {
  firstName: string;
  lastName?: string;
  phone: string;
  types: Option[];
  permissions: {
    viewMentor: boolean;
    viewMonthlyUpdates: boolean;
    viewSelfAssessmentData: boolean;
  };
}

const typeOptions: Option[] = [
  { label: "Parent/Guardian", value: "Parent/Guardian" },
  { label: "Other Family", value: "Other Family" },
  { label: "Friend", value: "Friend" },
  { label: "Emergency Contact", value: "Emergency Contact" },
  {
    label: "Clinician/Medical Professional",
    value: "Clinician/Medical Professional",
  },
  { label: "School", value: "School" },
];

const permissionItems = [
  {
    id: "viewMentor",
    label: "View your Mentor",
    icon: UserIcon,
  },
  {
    id: "viewMonthlyUpdates",
    label: "View Monthly Updates",
    icon: StarIcon,
  },
  {
    id: "viewSelfAssessmentData",
    label: "View Self-Assessment Data",
    icon: FlagIcon,
  },
] as const;

// Phone number validation regex
// Matches:
// 1) Numbers with country code: +1234567890, +1-234-567-8900
// 2) Numbers without country code: 1234567890, 123-456-7890
const PHONE_REGEX = /^(\+\d{1,3}[-\s]?)?\d{3}[-\s]?\d{3}[-\s]?\d{4}$/;

const formatPhoneNumber = (phone: string) => {
  // Remove all non-digit characters except plus
  const cleaned = phone.replace(/[^\d+]/g, "");

  // If it already has a country code (starts with +), return as is
  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  // Otherwise, add +1 as country code
  return `+1${cleaned}`;
};

export function AddAccessRecordDialog({
  open,
  onOpenChange,
  menteeId,
}: AddAccessRecordDialogProps) {
  const { toast } = useToast();
  const { mutateAsync: createAccess, isPending } =
    useCreateTeenAccess(menteeId);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<AccessRecordFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      types: [],
      permissions: {
        viewMentor: false,
        viewMonthlyUpdates: false,
        viewSelfAssessmentData: false,
      },
    },
  });

  const handleClose = () => {
    reset();
    onOpenChange?.(false);
  };

  const onSubmit = async (data: AccessRecordFormData) => {
    try {
      await createAccess({
        firstName: data.firstName,
        lastName: data.lastName,
        phone: formatPhoneNumber(data.phone),
        types: data.types.map((t) => t.value),
        permissions: data.permissions,
      });
      handleClose();

      toast({
        title: "Successfully added access record",
      });
    } catch (error) {
      console.error(error);

      toast({
        title: "Failed to add access record",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className="pb-4">
          <DialogTitle>Add New Access</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                {...register("firstName", {
                  required: "First name is required",
                })}
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">
                  {errors.firstName.message}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="lastName">Last Name (Optional)</Label>
              <Input id="lastName" {...register("lastName")} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                placeholder="+1 (123) 456-7890"
                {...register("phone", {
                  required: "Phone number is required",
                  pattern: {
                    value: PHONE_REGEX,
                    message: "Please enter a valid phone number",
                  },
                  validate: {
                    validLength: (value) => {
                      const digits = value.replace(/[^\d]/g, "");
                      return (
                        (digits.length >= 10 && digits.length <= 15) ||
                        "Phone number must be between 10 and 15 digits"
                      );
                    },
                  },
                })}
              />
              {errors.phone && (
                <p className="text-sm text-red-500">{errors.phone.message}</p>
              )}
              {/* <p className="text-xs text-muted-foreground">
                Enter with or without country code (defaults to +1)
              </p> */}
            </div>

            <div className="grid gap-2">
              <Label>Roles</Label>
              <Controller
                name="types"
                control={control}
                rules={{ required: "Please select at least one role" }}
                render={({ field: { onChange, value } }) => (
                  <MultiSelect
                    options={typeOptions}
                    defaultValue={value}
                    onChange={onChange}
                    placeholder="Select roles..."
                  />
                )}
              />
              {errors.types && (
                <p className="text-sm text-red-500">{errors.types.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-base font-bold text-black">Access</h4>
            <div className="space-y-3">
              {permissionItems.map(({ id, label, icon: Icon }) => (
                <Controller
                  key={id}
                  control={control}
                  name={`permissions.${id}`}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex items-center justify-between transition-colors">
                      <div className="flex items-center space-x-3">
                        <Icon
                          className={cn(
                            "h-5 w-5 transition-colors",
                            value ? "text-[#2563EB]" : "text-[#ABABAB]",
                          )}
                        />
                        <Label
                          htmlFor={id}
                          className={cn(
                            "cursor-pointer text-sm font-medium transition-colors",
                            value ? "text-[#2563EB]" : "text-[#777777]",
                          )}
                        >
                          {label}
                        </Label>
                      </div>
                      <Switch
                        id={id}
                        checked={value}
                        onCheckedChange={onChange}
                        className={cn(
                          "data-[state=checked]:bg-[#2563EB]",
                          "h-6 w-11",
                          "[&>span]:h-5 [&>span]:w-5",
                        )}
                      />
                    </div>
                  )}
                />
              ))}
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !isDirty}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Access
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
